<template>
    <div class="inbox-menu-wrapper">
        <div 
            v-for="item in processedData"
            :key="item.submissionId"
            :class="['menu-item', { selected: item.submissionId === selectedSubmissionId }]"
            :data-submission-id="item.submissionId"
            @click="selectItem(item)"
        >
            <div class="menu-item-content">
                <div class="menu-item-header">
                    <span class="submission-number">#{{ item.submissionNo }}</span>
                    <span class="time-ago">{{ formatDateRelative(item.updatedAt) }}</span>
                </div>
                <div class="menu-item-text">
                    {{ item.menuText }}
                </div>
                <div class="menu-item-date">
                    {{ formatDate(item.updatedAt) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment/moment";

export default {
    name: "InboxMenu",
    props: {
        answersData: {
            type: Object,
            required: true,
        },
        selectedSubmissionId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            processedData: [],
            localSelectedId: null
        };
    },
    watch: {
        answersData: {
            immediate: true,
            deep: true,
            handler(newVal) {
                if(newVal && newVal.submissions) {
                    this.processData(newVal.submissions);
                    this.$nextTick(() => {
                        if(this.selectedSubmissionId) {
                            this.scrollToSelectedItem();
                        }
                    });
                }
            }
        },
        selectedSubmissionId(newVal) {
            if(newVal) {
                this.$nextTick(() => {
                    this.scrollToSelectedItem();
                });
            }
        }
    },
    mounted() {
        this.checkUrlSubmissionId();
    },
    methods: {
        processData(submissions) {
            this.processedData = submissions.map(item => ({
                submissionId: item.submission._id,
                submissionNo: item.submission.submission_no,
                menuText: this.generateMenuText(item),
                updatedAt: item.submission.createdAt,
                status: item.status
            }));
        },
        generateMenuText(item) {
            if (!item.submission.submissionText) {
                return "Yanıt metni bulunamadı";
            }
            const text = Array.isArray(item.submission.submissionText) 
                ? item.submission.submissionText.join(" ") 
                : item.submission.submissionText;
            return text;
        },
        checkUrlSubmissionId() {
            const submissionId = this.$route.query.submissionId;
            if(submissionId && this.processedData.length > 0) {
                const exists = this.processedData.some(i => i.submissionId === submissionId);
                if(exists) {
                    this.selectItemById(submissionId);
                }
            }
        },
        selectItemById(submissionId) {
            const item = this.processedData.find(i => i.submissionId === submissionId);
            if(item) {
                this.localSelectedId = submissionId;
                this.$emit("item-selected", submissionId);
                this.highlightSelected(item);
            }
        },
        highlightSelected(item) {
            this.$nextTick(() => {
                if(this.$el) {
                    const element = this.$el.querySelector(`[data-submission-id="${item.submissionId}"]`);
                    if(element) {
                        element.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'nearest' 
                        });
                    }
                }
            });
        },
        selectItem(item) {
            this.localSelectedId = item.submissionId;
            this.$emit("item-selected", item.submissionId);
            this.$router.push({
                query: {
                    ...this.$route.query,
                    submissionId: item.submissionId
                }
            });
        },
        formatDate(date) {
            return moment(date).format("DD MMM YYYY, HH:mm");
        },
        formatDateRelative(date) {
            const now = moment();
            const target = moment(date);
            const diffMinutes = now.diff(target, 'minutes');
            const diffHours = now.diff(target, 'hours');
            const diffDays = now.diff(target, 'days');

            if (diffMinutes < 1) return 'Şimdi';
            if (diffMinutes < 60) return `${diffMinutes}dk önce`;
            if (diffHours < 24) return `${diffHours}sa önce`;
            if (diffDays < 7) return `${diffDays}g önce`;
            return target.format("DD MMM");
        },
        scrollToSelectedItem() {
            const element = this.$el.querySelector(`[data-submission-id="${this.selectedSubmissionId}"]`);
            if(element) {
                element.scrollIntoView({
                    behavior: 'auto',
                    block: 'center'
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.inbox-menu-wrapper {
    height: 100%;
    overflow-y: auto;
    padding: 0.5rem;
    scrollbar-width: thin;
    scrollbar-color: #c7d0d7 #fafbfc;

    &::-webkit-scrollbar {
        width: 5px;
    }

    &::-webkit-scrollbar-track {
        background: #fafbfc;
    }

    &::-webkit-scrollbar-thumb {
        background: #c7d0d7;
        border-radius: 3px;

        &:hover {
            background: #a6b3bd;
        }
    }
}

.menu-item {
    margin-bottom: 0.375rem;
    cursor: pointer;
    transition: all 0.15s ease;

    &:last-child {
        margin-bottom: 0;
    }

    &:hover {
        .menu-item-content {
            background: linear-gradient(to right, #f6f8f9 0%, #ffffff 100%);
            border-color: #c7d0d7;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
            transform: translateX(2px);
        }

        .submission-number {
            color: #121317;
        }

        .menu-item-text {
            color: #121317;
        }
    }

    &.selected {
        .menu-item-content {
            background: linear-gradient(to right, #edf1f4 0%, #f6f8f9 100%);
            border-color: #5a5f72;
            border-left-width: 3px;
            box-shadow: 0 2px 8px rgba(90, 95, 114, 0.15);
        }

        .submission-number {
            color: #121317;
            font-weight: 700;
            background: #d1dbe0;
            padding: 0.125rem 0.375rem;
            border-radius: 3px;
        }

        .menu-item-text {
            color: #121317;
            font-weight: 600;
        }

        .time-ago {
            color: #5a5f72;
            font-weight: 500;
        }
    }

    .menu-item-content {
        padding: 0.625rem;
        background: linear-gradient(to right, #ffffff 0%, #fafbfc 100%);
        border: 1px solid #e8edf1;
        border-left: 1px solid #e8edf1;
        border-radius: 5px;
        transition: all 0.15s ease;
    }

    .menu-item-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.375rem;
    }

    .submission-number {
        font-size: 0.6875rem;
        font-weight: 600;
        color: #5a5f72;
        transition: all 0.15s ease;
        letter-spacing: -0.01em;
    }

    .time-ago {
        font-size: 0.625rem;
        color: #9a9da9;
        font-weight: 500;
    }

    .menu-item-text {
        font-size: 0.75rem;
        color: #121317;
        font-weight: 500;
        line-height: 1.4;
        margin-bottom: 0.375rem;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        transition: all 0.15s ease;
    }

    .menu-item-date {
        font-size: 0.625rem;
        color: #9a9da9;
        font-weight: 500;
        padding-top: 0.375rem;
        border-top: 1px solid #e8edf1;
    }
}
</style>
